import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { IInfinitePageParams, IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import { IRegisterUserBody, IUserLightList, IUserResponse } from './types';

export const useGetClientUserAccounts = (userId: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<{ id: string; labelName?: string | null }[], DefaultError>({
    queryKey: ['client-user-accounts', userId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `ClientUserManagement/UserAccounts`,
        params: { userId },
      });

      return responseHandler(response, {
        groupName: 'useGetClientUserAccounts',
        notFoundPath: '/admin/client-users',
      });
    },
  });
};
export const useGetClientUsersLightList = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IUserLightList[], DefaultError>({
    queryKey: ['client-users-light-list'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `ClientUserManagement/LightList`,
        params: { query: {} },
      });

      return responseHandler(response, {
        groupName: 'useGetClientUsersLightList',
        notFoundPath: '/admin/client-users',
      });
    },
  });
};

export const useGetClientUsers = (params?: IInfinitePageParams) => {
  const { responseHandler } = useResponseHandler();

  const { getNextPageParam, setTotalPageCount, infiniteHandler } =
    useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IUserResponse[], DefaultError>({
    queryKey: ['client-users-infinite-list', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'ClientUserManagement/GetUsers',
        params: { ...params, itemCount, page: pageParam },
      });

      const value = await responseHandler<IInfinitePageResponse<IUserResponse>>(
        response,
        {
          groupName: `useGetClientUsers - ${pageParam}`,
        }
      );

      return infiniteHandler<IUserResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetClientUser = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IUserResponse, DefaultError>({
    queryKey: ['clientUserManagement', 'user'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `ClientUserManagement/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useGetClientUser',
        notFoundPath: '/admin/client-users',
      });
    },
  });
};

export const useRegisterClientUser = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useRegisterClientUser');
  return useMutation<IUserResponse, DefaultError, IRegisterUserBody>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('user.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: 'ClientUserManagement/Register',
        body,
      });

      return responseHandler(response, {
        groupName: 'useRegisterClientUser',
        notFoundPath: '/admin/client-users',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.userName ?? i18next.t('user.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteClientUser = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteClientUser');

  return useMutation<boolean, DefaultError, string>({
    mutationFn: async (userEmail: string) => {
      loading(
        i18next.t('service-code.delete.loading', {
          what: i18next.t('user.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: 'ClientUserManagement/DeleteClientUser',
        body: { email: userEmail },
      });

      return responseHandler(response, {
        groupName: `useDeleteClientUser`,
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('user.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useActivateClientUser = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useActivateClientUser');

  return useMutation<boolean, DefaultError, void>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.active.loading', {
          what: i18next.t('user.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'ClientUserManagement/ActivateClientUser',
        body: { userId: id },
      });

      return responseHandler(response, {
        groupName: `useActivateClientUser - ${id}`,
      });
    },

    onSuccess: data =>
      success(
        i18next.t('service-message.active.success', {
          what: i18next.t('user.what'),
        })
      ),
    onError: dismiss,
  });
};

export const useDeactivateClientUser = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useActivateClientUser');

  return useMutation<boolean, DefaultError, void>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.deactivate.loading', {
          what: i18next.t('user.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'ClientUserManagement/DeactivateClientUser',
        body: { userId: id },
      });

      return responseHandler(response, {
        groupName: `useDeactivateClientUser`,
      });
    },

    onSuccess: data =>
      success(
        i18next.t('service-message.deactivate.success', {
          what: i18next.t('user.what'),
        })
      ),
    onError: dismiss,
  });
};
